import { useState, useEffect } from 'react'
import { Play, Square, CheckCircle, Clock, TrendingUp, AlertCircle, Activity } from 'lucide-react'
import { api, MonitorStatus, Stats, HistoryRecord } from '../lib/api'
import StatusCard from '../components/StatusCard'
import TimelineChart from '../components/TimelineChart'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'

interface CheckHistory {
  timestamp: string
  detected: boolean
  times_count: number
}

export default function Dashboard() {
  const [status, setStatus] = useState<MonitorStatus | null>(null)
  const [stats, setStats] = useState<Stats | null>(null)
  const [history, setHistory] = useState<CheckHistory[]>([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchData = async () => {
    try {
      const [statusData, statsData, historyData] = await Promise.all([
        api.getStatus(),
        api.getStats(),
        api.getHistory(50).catch(() => []) // Fallback to empty array if history fails
      ])
      setStatus(statusData)
      setStats(statsData)
      // Map HistoryRecord to CheckHistory format
      const mappedHistory: CheckHistory[] = historyData.map((record: HistoryRecord) => ({
        timestamp: record.timestamp,
        detected: record.appointments_found,
        times_count: record.times_count
      }))
      setHistory(mappedHistory)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar datos')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, 10000) // Refresh every 10s
    return () => clearInterval(interval)
  }, [])

  const handleStart = async () => {
    setActionLoading(true)
    try {
      await api.startMonitor()
      await fetchData()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al iniciar monitor')
    } finally {
      setActionLoading(false)
    }
  }

  const handleStop = async () => {
    setActionLoading(true)
    try {
      await api.stopMonitor()
      await fetchData()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al detener monitor')
    } finally {
      setActionLoading(false)
    }
  }

  const handleCheckNow = async () => {
    setActionLoading(true)
    try {
      await api.checkNow()
      await fetchData()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error al ejecutar revisión')
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          <p className="text-red-800">Error: {error}</p>
        </div>
      </div>
    )
  }

  const formatUptime = (seconds: number | null) => {
    if (!seconds) return 'N/A'
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${hours}h ${minutes}m`
  }

  return (
    <div className="space-y-6">
      {/* Control Panel */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Panel de Control</h2>
            <p className="text-sm text-gray-500 mt-1">
              Estado actual del monitor de citas consulares
            </p>
          </div>
          <div className="flex space-x-3">
            {status?.is_running ? (
              <button
                onClick={handleStop}
                disabled={actionLoading}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                <Square className="h-4 w-4 mr-2" />
                Detener
              </button>
            ) : (
              <button
                onClick={handleStart}
                disabled={actionLoading}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                <Play className="h-4 w-4 mr-2" />
                Iniciar
              </button>
            )}
            <button
              onClick={handleCheckNow}
              disabled={actionLoading}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Revisar Ahora
            </button>
          </div>
        </div>

        {/* Status Indicator */}
        <div className="mt-6 flex items-center">
          <div className={`h-3 w-3 rounded-full mr-2 ${status?.is_running ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
          <span className="font-medium text-gray-700">
            {status?.is_running ? 'Monitor Activo' : 'Monitor Detenido'}
          </span>
          {status?.last_check && (
            <span className="ml-4 text-sm text-gray-500">
              Última revisión: {formatDistanceToNow(new Date(status.last_check), { addSuffix: true, locale: es })}
            </span>
          )}
        </div>

        {/* Current Target URL */}
        {status?.target_url && (
          <div className="mt-4 p-3 bg-gray-50 rounded-md border border-gray-200">
            <p className="text-xs font-medium text-gray-500 mb-1">URL Monitoreada:</p>
            <p className="text-sm text-gray-700 break-all">{status.target_url}</p>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatusCard
          title="Total de Revisiones"
          value={stats?.total_checks || 0}
          icon={Clock}
          color="blue"
        />
        <StatusCard
          title="Citas Encontradas"
          value={stats?.appointments_found || 0}
          icon={CheckCircle}
          color="green"
        />
        <StatusCard
          title="Revisiones (24h)"
          value={stats?.checks_last_24h || 0}
          icon={TrendingUp}
          color="yellow"
        />
        <StatusCard
          title="Tiempo Activo"
          value={formatUptime(status?.uptime_seconds || null)}
          icon={Clock}
          color="gray"
          subtitle={`Tasa de éxito: ${stats?.success_rate_percent.toFixed(1)}%`}
        />
      </div>

      {/* Timeline Chart */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Activity className="h-5 w-5 text-primary-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Línea de Tiempo de Revisiones</h3>
          </div>
          <a href="/history" className="text-sm text-primary-600 hover:underline">
            Ver historial completo →
          </a>
        </div>
        <TimelineChart data={history} />
      </div>
    </div>
  )
}
